import { Grid } from "@mui/material";
import React, { useState } from "react";
import { Button, Card, Col, Row } from "react-bootstrap";
import { PredictRace, SettingModule } from "../modals/allModal";
import useDialogState from "../../hooks/useDialog";
import useMenuState from "../../hooks/useMenuState";
import ImageService from "../../services/setting.service";
import { useFormik } from "formik";
import { useEffect } from "react";
import * as yup from "yup";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { FaLongArrowAltLeft } from "react-icons/fa";

const Setting = () => {
  const location = useLocation();
  const selectedType = location.search.split("=")[1];
  const id = location.search.split("=")[2];

  const [deleteRace, setDeleteRace] = useState(false);
  const [update, setUpdate] = useState(false);
  const handleCloseRace = () => setDeleteRace(false);
  
  const [loader, setLoader] = useState(false);
  const [data, setData] = useState([]);
  const [showLink, setShowLink] = useState("");
  const [deleteId, setDeleteId] = useState("");
  const [languageImageId, setLanguageImageId] = useState("");
  const [currentIndex, setCUrrentIndex] = useState();
  const [loading, setLoading] = useState(false);
  const [initialLanguageValues, setInitialLanguageValues] = useState([]); // Track initial languages for update
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const isUniqueIndex = (value) => {
    return !data?.some(
      (item) => item.index === value && item.index !== Number(currentIndex)
    );
  };

  const initialValue = {
    name: "",
    files: null,
    index: "",
    language: [],
    languageImagePairs: [], // Array of {language: {...}, file: File}
  };

  const validationSchema = yup.object().shape({
    name: yup.string().required("Name is required"),
    index: yup
      .number()
      .typeError("Index must be a number")
      .required("Index is required")
      .integer("Index must be an integer")
      .max(100, "you can add index upto 100")
      .test("unique-index", "Index already exists", (value) => {
        return isUniqueIndex(value);
      }),
    languageImagePairs: yup
      .array()
      .min(1, "At least one language-image pair is required")
      .test("same-length", "Each language must have a corresponding image", (value) => {
        if (!value || value.length === 0) return false;
        return value.every((pair) => pair.language && (pair.file || pair.fileUrl));
      })
      .test("unique-languages", "Each language can only be selected once", (value) => {
        if (!value || value.length === 0) return true;
        const languageValues = value.map((pair) => pair.language?.value).filter(Boolean);
        return new Set(languageValues).size === languageValues.length;
      }),
  });

  const {
    open: modalShow,
    handleClickOpen: handleDialogOpen,
    handleClose: handleDialogClose,
  } = useDialogState();
  const [openCity, handleOpenCity, handleCloseCity] = useMenuState(false);

  const getAllUser = async () => {
    setLoading(true);
    const response = await ImageService.getAllImage(selectedType.toUpperCase());
    setData(id ? response.data.data.find((item) => item._id === id) : response.data.data || []);
    setLoading(false);
  };

  useEffect(() => {
    getAllUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);

  const submit = async (values, action) => {
    const { id, name, link, index, languageImagePairs } = values;

    if (values.id) {
      try {
        setLoader(true);
        const fileList = new FormData();
        // For update, only append File objects (new uploads), skip URL strings (existing images)
        if (languageImagePairs && languageImagePairs.length > 0) {
          languageImagePairs.forEach((pair, idx) => {
            // Only append if it's a File object, not a URL string
            if (pair.file && pair.file instanceof File) {
              fileList.append(`files`, pair.file);
            }
          });
        }
        // Languages are included in the pairs structure
        // Only include newly added languages (not in initial set) for update
        const allLanguages = languageImagePairs.map((pair) => pair.language?.value).filter(Boolean);
        const newLanguages = allLanguages.filter(lang => !initialLanguageValues.includes(lang));
        await ImageService.UpdateImage(id, decodeURIComponent(name), fileList, link, index, selectedType.toUpperCase(), newLanguages, languageImagePairs.map((pair) => pair.fileUrl).filter(Boolean));
        setLoader(false);
        handleDialogClose();
        getAllUser();
        action.resetForm();
      } catch (error) {
        console.error("create race error", error);
        setLoader(false);
      }
    } else {
      try {
        const fileList = new FormData();
        setLoader(true);
        // Append all files in order
        languageImagePairs.forEach((pair, idx) => {
          if (pair.file) {
            fileList.append(`files`, pair.file);
          }
        });
        const languages = languageImagePairs.map((pair) => pair.language?.value).filter(Boolean);
        // await ImageService.AddImage(name, fileList, link, index, languages);
        // Note: languages variable is prepared for API call when service is updated
        await ImageService.AddImage(decodeURIComponent(name), fileList, link, index, languages, selectedType.toUpperCase());
        setLoader(false);
        handleDialogClose();
        getAllUser();
        action.resetForm();
      } catch (error) {
        console.error("create race error", error);
        setLoader(false);
      }
    }
  };

  const formik = useFormik({
    initialValues: initialValue,
    validationSchema: validationSchema,
    onSubmit: submit,
  });

  const handleOpen = async (id, index) => {
    setCUrrentIndex(index);
    setShowLink(index);
    handleDialogOpen();
    if (id) {
      setUpdate(true);
      const response = await ImageService.GetImage(id);
      const result = response.data.data;
      formik.setFieldValue("id", result._id);
      formik.setFieldValue("name", result.name);
      formik.setFieldValue("index", result.index);
      formik.setFieldValue("files", result.imgUrl);
      formik.setFieldValue("link", result.link);
      // If result has imagesAndLanguages data, populate pairs with proper image URLs
      if (result.imagesAndLanguages && Array.isArray(result.imagesAndLanguages) && result.imagesAndLanguages.length > 0) {
        const pairs = result.imagesAndLanguages.map((item) => {
          const langValue = item.language || item.countryCode;
          // Normalize the value for comparison (handle string/number, with/without "+")
          const normalizedValue = String(langValue).trim();
          // Try multiple matching strategies
          const langObj = languageStatus.find((l) => {
            const normalizedLangValue = String(l.value).trim();
            // Exact match
            if (normalizedLangValue === normalizedValue) return true;
            // Match with/without "+" prefix
            const langWithoutPlus = normalizedLangValue.replace(/^\+/, '');
            const valueWithoutPlus = normalizedValue.replace(/^\+/, '');
            if (langWithoutPlus === valueWithoutPlus) return true;
            // Match with "+" added
            if (`+${langWithoutPlus}` === normalizedValue || `+${valueWithoutPlus}` === normalizedLangValue) return true;
            return false;
          });
          // Ensure we always use the language object with proper label, not the country code
          if (!langObj) {
            console.warn(`Language not found for value: ${langValue}. Available values:`, languageStatus.map(l => l.value));
          }
          return {
            language: langObj || { value: langValue, label: langValue }, // Fallback only if absolutely necessary
            file: item.imgUrl || result.imgUrl, // Use specific image URL for this language or fallback to main image
            fileUrl: item.imgUrl || result.imgUrl, // Store URL for preview
            _id: item._id, // Store the languageImageId for deletion
          };
        });
        formik.setFieldValue("languageImagePairs", pairs);
        formik.setFieldValue("language", pairs.map((p) => p.language));
        // Store initial language values for update comparison
        const initialValues = pairs.map((p) => p.language?.value || p.language);
        setInitialLanguageValues(initialValues);
      } else if (result.language && Array.isArray(result.language)) {
        // Fallback to language array if imagesAndLanguages is not available
        const pairs = result.language.map((langValue) => {
          // Normalize the value for comparison (handle string/number, with/without "+")
          const normalizedValue = String(langValue).trim();
          // Try multiple matching strategies
          const langObj = languageStatus.find((l) => {
            const normalizedLangValue = String(l.value).trim();
            // Exact match
            if (normalizedLangValue === normalizedValue) return true;
            // Match with/without "+" prefix
            const langWithoutPlus = normalizedLangValue.replace(/^\+/, '');
            const valueWithoutPlus = normalizedValue.replace(/^\+/, '');
            if (langWithoutPlus === valueWithoutPlus) return true;
            // Match with "+" added
            if (`+${langWithoutPlus}` === normalizedValue || `+${valueWithoutPlus}` === normalizedLangValue) return true;
            return false;
          });
          // Ensure we always use the language object with proper label, not the country code
          if (!langObj) {
            console.warn(`Language not found for value: ${langValue}. Available values:`, languageStatus.map(l => l.value));
          }
          return {
            language: langObj || { value: langValue, label: langValue }, // Fallback only if absolutely necessary
            file: result.imgUrl, // Use existing image URL
            fileUrl: result.imgUrl, // Store URL for preview
          };
        });
        formik.setFieldValue("languageImagePairs", pairs);
        formik.setFieldValue("language", pairs.map((p) => p.language));
        // Store initial language values for update comparison
        const initialValues = pairs.map((p) => p.language?.value || p.language);
        setInitialLanguageValues(initialValues);
      }
    } else {
      setUpdate(false);
      formik.setFieldValue("name", "");
      formik.setFieldValue("files", "");
      formik.setFieldValue("link", "");
      formik.setFieldValue("index", "");
      formik.setFieldValue("languageImagePairs", []);
      formik.setFieldValue("language", []);
      setInitialLanguageValues([]); // Reset initial languages
    }
  };

  const handleFileUpload = (index, e) => {
    const selectedFile = e.currentTarget.files[0];
    const pairs = [...formik.values.languageImagePairs];
    if (pairs[index]) {
      pairs[index].file = selectedFile;
      formik.setFieldValue("languageImagePairs", pairs);
    }
  };

  const handelDelete = (id) => {
    setLanguageImageId(undefined);
    setDeleteId(id);
    setDeleteRace(true);
  };

  const handelDeleteLanguage = (id, imageId) => {
    setDeleteId(id);
    setLanguageImageId(imageId);
    setDeleteRace(true);
  };

  const changeLanguage = (selectedLanguages) => {
    const currentPairs = [...formik.values.languageImagePairs];
    const newPairs = selectedLanguages.map((lang, index) => {
      // Try to preserve existing file if language already exists
      const existingPair = currentPairs.find((pair) => pair.language?.value === lang.value);
      return {
        language: lang,
        file: existingPair?.file || null,
      };
    });
    formik.setFieldValue("languageImagePairs", newPairs);
    formik.setFieldValue("language", selectedLanguages);
  };
  const languageStatus = [
    {
      label: "ENGLISH",
      value: "+44",
    },
    {
      label: "FRENCH",
      value: "+33",
    },
    {
      label: "SPANISH",
      value: "34",
    },
    {
      label: "GERMAN",
      value: "+49",
    },
    {
      label: "ITALIAN",
      value: "+39",
    },
    {
      label: "ARABIC",
      value: "+966",
    },
    {
      label: "MANDARIN",
      value: "+86",
    },
    {
      label: "BAHASA",
      value: "+62",
    },
    {
      label: "JAPANESE",
      value: "+81",
    },
    {
      label: "HINDI",
      value: "+91",
    },
    {
      label: "KOREAN",
      value: "+82",
    },
    {
      label: "TURKISH",
      value: "+90",
    },
    {
      label: "THAI",
      value: "+66",
    },
    {
      label: "VIETNAMESE",
      value: "+84",
    },
    {
      label: "RUSSIAN",
      value: "+7",
    },
    {
      label: "GREEK",
      value: "+30",
    },

  ];


  const handleBack = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("id"); // 👈 remove id only

    setSearchParams(params, { replace: true });
  };


  return (
    <>
      <div className="page-header2"></div>
      <div className="row">
        {!id && <Col sm={12} md={6} lg={6} xl={3}>
          <Card className="custom-card">
            <Card.Body>
              <div className="card-order">
                <label className="main-content-label font-weight-bold mb-3 pt-1">
                  Total Race Image
                </label>
                <h2 className="text-end card-item-icon card-icon">
                  <i className="fas fa-gear  icon-size float-start text-primary"></i>
                </h2>
              </div>
            </Card.Body>
          </Card>
        </Col>}

        {id && (
          <Col className="ms-auto mt-auto d-flex pointer" style={{ justifyContent: "start" }} onClick={handleBack}>
            <Card className="custom-card">
              <Card.Body>
                <FaLongArrowAltLeft
                  // onClick={handleBack}
                  style={{ fontSize: "20px", cursor: "pointer" }}
                />
              </Card.Body>
            </Card>
          </Col>
        )}

        <Col
          sm={12}
          md={6}
          lg={6}
          xl={4}
          className="ms-auto mt-auto d-flex"
          style={{ justifyContent: "end" }}>
          <div className="custom-card text-end  pointer me-5">
            {/* <Button className="text-white" onClick={() => handleOpen("")}>
              <i className="fas fa-plus"></i> &nbsp; Add Race Image
            </Button> */}
            {modalShow && (
              <PredictRace
                languageStatus={languageStatus}
                changeLanguage={changeLanguage}
                show={modalShow}
                onHide={handleDialogClose}
                loader={loader}
                update={update}
                formik={formik}
                handleFileUpload={handleFileUpload}
                showLink={showLink}
                openCity={openCity}
                handleOpenCity={handleOpenCity}
                handleCloseCity={handleCloseCity}
              />
            )}
          </div>
        </Col>
        {!id && <Col
          sm={12}
          md={6}
          lg={6}
          xl={4}
          className="ms-auto mt-auto d-flex"
          style={{ justifyContent: "end" }}>
          <div className="custom-card text-end  pointer me-5">
            <Button className="text-white" onClick={() => handleOpen("")}>
              <i className="fas fa-plus"></i> &nbsp; Add Race Image
            </Button>
          </div>
        </Col>}
      </div>
      <Row className="row m-auto">
        {data && !loading
          ? id ? data?.imagesAndLanguages?.map((card, index) => (
            <Col sm={12} md={6} lg={4} xl={4} xxl={3} key={index}>
              <Grid
                sx={{
                  display: "flex",
                  justifyContent: {
                    sm: "center",
                    xs: "center",
                    md: "start",
                    marginBottom: "15px",
                  },
                }}>
                <Card
                  onClick={(() => card?.imagesAndLanguages?.length > 0 && navigate(
                    `/setting?type=${selectedType}&id=${card._id}`,
                    { replace: true }
                  ))}

                  className="custom-card pointer"
                  style={{ height: "250px", width: "350px" }}>
                  <img
                    src={card.imgUrl ? card.imgUrl : card?.imagesAndLanguages[0]?.imgUrl}
                    className="rounded-lg img-fit-cover pos-relative"
                    alt="img"
                  />
                  <button
                    style={{
                      position: "absolute",
                      bottom: 0,
                      right: 10,
                    }}
                    className="text-white ms-3 mb-2 btn btn-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handelDeleteLanguage(card?._id, data?._id);
                    }}>
                    <i className="fas fa-trash"></i>
                  </button>
                  {/* <button
                    style={{
                      position: "absolute",
                      bottom: 0,
                      right: 60,
                    }}
                    className="text-white ms-3 mb-2 btn btn-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpen(card._id, card?.index);
                    }}>
                    <i className="fa fa-pencil"></i>
                  </button> */}
                  <Card.Body className="pos-absolute card-body2 d-flex flex-column gap-2 ">
                    <div
                      className="card-order bg-000 width-fit"
                      style={{ border: "1px solid #000", width: "fit-content" }}>
                      <label
                        className="main-content-label font-weight-bold mb-0 pointer"
                        style={{ border: "1px solid #000 !importan" }}>
                        {data.name}
                      </label>
                    </div>
                    {/* <div
                      className="card-order bg-000 !width-fit !height-fit"
                      style={{ border: "1px solid #000" }}> */}
                    <img
                      src={card.countryImage ? card.countryImage : ""}
                      alt="img"
                      className="main-content-label font-weight-bold mb-0 pointer img-thumbnail img-fit-cover"
                      style={{ border: "1px solid #000 !importan", width: "50px", height: "50px" }}>
                    </img>
                    {/* </div> */}


                  </Card.Body>

                </Card>
              </Grid>
            </Col>
          )) : !loading && data?.length > 0 && data?.map((card, index) => (
            <Col sm={12} md={6} lg={4} xl={4} xxl={3} key={index}>
              <Grid
                sx={{
                  display: "flex",
                  justifyContent: {
                    sm: "center",
                    xs: "center",
                    md: "start",
                    marginBottom: "15px",
                  },
                }}>
                <Card
                  onClick={(() => card?.imagesAndLanguages?.length > 0 && navigate(`/setting?type=${selectedType}&id=${card._id}`, {
                    replace: true,
                  }))}
                  className="custom-card pointer"
                  style={{ height: "250px", width: "350px" }}>
                  <img
                    src={card.imgUrl ? card.imgUrl : card?.imagesAndLanguages[0]?.imgUrl}
                    className="rounded-lg img-fit-cover pos-relative"
                    alt="img"
                  />
                  <button
                    style={{
                      position: "absolute",
                      bottom: 0,
                      right: 10,
                    }}
                    className="text-white ms-3 mb-2 btn btn-primary"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handelDelete(card._id);
                    }}>
                    <i className="fas fa-trash"></i>
                  </button>
                  <button
                    style={{
                      position: "absolute",
                      bottom: 0,
                      right: 60,
                    }}
                    className="text-white ms-3 mb-2 btn btn-primary"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpen(card._id, card?.index);
                    }}>
                    <i className="fa fa-pencil"></i>
                  </button>
                  <Card.Body className="pos-absolute card-body2 d-flex flex-column gap-2">
                    <div
                      className="card-order bg-000"
                      style={{ border: "1px solid #000", width: "fit-content" }}>
                      <label
                        className="main-content-label font-weight-bold mb-0 pointer"
                        style={{ border: "1px solid #000 !importan" }}>
                        {card.name}
                      </label>
                    </div>
                    <div>
                      {card?.imagesAndLanguages?.length > 0 && card?.imagesAndLanguages
                        .filter((item) => item.countryImage)
                        .map((item, idx) => (
                          <img
                            key={idx}
                            src={item.countryImage}
                            alt="img"
                            className="main-content-label font-weight-bold mb-0 pointer img-thumbnail img-fit-cover me-2"
                            style={{ border: "1px solid #000 !importan", width: "35px", height: "35px" }}>
                          </img>
                        ))}
                    </div>
                  </Card.Body>
                </Card>
              </Grid>
            </Col>
          ))
          : loading && (
            <div className="d-flex justify-content-center align-items-center  ">
              <div className="spinner-border text-primary mt-5" role="status">
                {/* <span className="visually-hidden">Loading...</span> */}
              </div>
            </div>
          )}
      </Row>
      {/* </div> */}
      {deleteRace && (
        <SettingModule
          show={deleteRace}
          onHide={handleCloseRace}
          id={deleteId}
          languageImageId={languageImageId}
          getAllData={getAllUser}
        />
      )}
    </>
  );
};

export default Setting;
